// Considered: `CompactTimelineMolecule.__date-separator` (inline div, now retired — deleted in
// Phase 6, specs/stream-trajectory-tools.md § Design System Reuse Audit). Adds a collapse chevron
// and an optional note slot ("new since you were here").
//
// Source: design/metaspace-tools/metaspace-tools-v2.jsx:6-15 (`MSDayChip`).
//
// Phase 6 accessibility sweep: day-group collapse is a native `<details>/<summary>` — `<summary>`
// is keyboard-focusable and Enter/Space-toggleable without any hand-rolled key handling, and the
// day's entries are its `children` rather than a sibling the caller conditionally renders.
//
// `toggle` fires on EVERY change of the `open` attribute, including the one React itself writes
// when re-rendering with a new `collapsed`. Forwarding that echo to `onToggle` makes a controlled
// `<details>` self-oscillate: React writes `open` -> browser fires `toggle` -> parent flips state
// -> React writes `open` -> ... So only report a toggle when the DOM has actually diverged from
// the `collapsed` prop, i.e. when the user drove the change.

import React from 'react';
import GlyphAtom from '../Glyph/GlyphAtom';
import './StreamDayChipAtom.scss';

export interface StreamDayChipAtomProps {
  /** Day label, e.g. "AUG 20". */
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  /** e.g. "new since you were here". */
  note?: string;
  className?: string;
  /** The day's entries — rendered inside the `<details>`, hidden natively when collapsed. */
  children?: React.ReactNode;
}

const StreamDayChipAtom: React.FC<StreamDayChipAtomProps> = ({ label, collapsed, onToggle, note, className, children }) => {
  const handleToggle = (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    // Only a user-driven toggle leaves the DOM disagreeing with `collapsed`; React's own
    // attribute write echoes back already in agreement, and must not be forwarded.
    if (event.currentTarget.open === collapsed) onToggle();
  };

  return (
    <details
      className={['stream-day-chip-atom__group', className].filter(Boolean).join(' ')}
      open={!collapsed}
      onToggle={handleToggle}
    >
      <summary className="stream-day-chip-atom focus-ring" aria-expanded={!collapsed}>
        <GlyphAtom glyph={collapsed ? 'lucide:chevron-right' : 'lucide:chevron-down'} className="stream-day-chip-atom__chevron" />
        <span className="stream-day-chip-atom__label">{label}</span>
        {note && <span className="stream-day-chip-atom__note">{note}</span>}
      </summary>
      {children}
    </details>
  );
};

StreamDayChipAtom.displayName = 'StreamDayChipAtom';

export default StreamDayChipAtom;
